// Server-generated, persisted notifications (calendar reminders, NYSE
// open/close/heads-up, new feed entries — see docs/notifications.md).
// Read/deleted state lives only on the server, so every connected client
// converges on it, delivered over the stream `center.tsx`'s
// `NotificationsProvider` holds open. See docs/notifications.md's
// "Multi-device model" and "Streaming".

import type { AppNotification, NotificationsListResponse } from "@/notifications/model/wire.js";
import { api, qk, reportAppError } from "@/platform/client";
import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";

function invalidateNotifications(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: qk.notifications.all });
}

type Cached = [readonly unknown[], unknown][];

const isList = (data: unknown): data is NotificationsListResponse =>
  Array.isArray((data as NotificationsListResponse | undefined)?.rows);

/** Marks notifications read in every cached list and in the count before the
 *  server answers, and returns what was cached, to put back if it refuses. */
async function readAhead(qc: QueryClient, ids: readonly number[] | "all"): Promise<Cached> {
  await qc.cancelQueries({ queryKey: qk.notifications.all });
  const cached = qc.getQueriesData({ queryKey: qk.notifications.all });
  const readAt = new Date().toISOString();
  const marks = (n: AppNotification) => n.readAt == null && (ids === "all" || ids.includes(n.id));
  const marked = new Set<number>();
  for (const [key, data] of cached) {
    if (!isList(data)) continue;
    // A list key is ["notifications", unreadOnly, limit, offset].
    const unreadOnly = key[1] === true;
    const rows = data.rows.flatMap((n) => {
      if (!marks(n)) return [n];
      marked.add(n.id);
      return unreadOnly ? [] : [{ ...n, readAt }];
    });
    qc.setQueryData(key, { rows, total: data.total - (data.rows.length - rows.length) });
  }
  qc.setQueryData<{ count: number }>(
    qk.notifications.unreadCount,
    (c) => c && { count: ids === "all" ? 0 : Math.max(0, c.count - marked.size) },
  );
  return cached;
}

function putBack(qc: QueryClient, cached: Cached | undefined, err: unknown) {
  for (const [key, data] of cached ?? []) qc.setQueryData(key, data);
  reportAppError(err);
}

interface NotificationsParams {
  unreadOnly?: boolean;
  limit?: number;
  /** Offset pagination — the /notifications page's page-number control. */
  offset?: number;
}

function notificationsQueryOptions(params: NotificationsParams) {
  const key = [params.unreadOnly ?? null, params.limit ?? null, params.offset ?? null];
  const search = new URLSearchParams();
  if (params.unreadOnly) search.set("unreadOnly", "true");
  if (params.limit) search.set("limit", String(params.limit));
  if (params.offset) search.set("offset", String(params.offset));
  const qs = search.toString();
  return {
    queryKey: qk.notifications.list(key),
    queryFn: () => api.get<NotificationsListResponse>(`/notifications${qs ? `?${qs}` : ""}`),
  };
}

/** `{ rows, total }` — the unread stack the toast overlay renders, the
 *  bell's recent slice, or a page of the /notifications page's full list
 *  (`total` matches the current `unreadOnly` filter, for its pagination).
 *  Named `useNotificationsList`, not `useNotifications`, to avoid colliding
 *  with `center.tsx`'s `useNotifications()` context hook (the
 *  ephemeral-toast API). */
export function useNotificationsList(
  params: NotificationsParams = {},
  options?: { enabled?: boolean },
) {
  return useQuery({ ...notificationsQueryOptions(params), enabled: options?.enabled });
}

export function useUnreadNotificationCount() {
  return useQuery({
    queryKey: qk.notifications.unreadCount,
    queryFn: () => api.get<{ count: number }>("/notifications/unread-count"),
  });
}

/** Marking read shows at once, and is undone if the server refuses it. */
export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, read }: { id: number; read: boolean }) =>
      api.patch(`/notifications/${id}`, { read }),
    onMutate: ({ id, read }) => (read ? readAhead(qc, [id]) : undefined),
    onError: (err, _vars, cached) => putBack(qc, cached, err),
    onSettled: () => invalidateNotifications(qc),
  });
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ updated: number }>("/notifications/read-all"),
    onMutate: () => readAhead(qc, "all"),
    onError: (err, _vars, cached) => putBack(qc, cached, err),
    onSettled: () => invalidateNotifications(qc),
  });
}

export function useDeleteNotification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.del(`/notifications/${id}`),
    onSuccess: () => invalidateNotifications(qc),
  });
}

export function useNotificationRetentionDays() {
  return useQuery({
    queryKey: qk.settings.notificationRetentionDays,
    queryFn: () => api.get<{ days: number }>("/settings/notification-retention-days"),
  });
}

export function useUpdateNotificationRetentionDays() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (days: number) =>
      api.put<{ days: number }>("/settings/notification-retention-days", { days }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.settings.notificationRetentionDays }),
  });
}
